import { useCallback, useRef, useState } from 'react';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';
import { track, type EventProps } from '@/shared/analytics';
import { downloadFile } from '@/shared/utils/downloadFile';

export interface ServerPdfDownloadRequest {
  /** Identifies the item. A render for one key never blocks or marks another. */
  key: string;
  fetchBlob: () => Promise<Blob>;
  fileName: string;
  analytics: Omit<EventProps<'document_downloaded'>, 'format' | 'downloader'>;
}

export interface ServerPdfDownloadMessages {
  preparing: string;
  failed: string;
}

export interface UseServerPdfDownloadResult {
  download: (request: ServerPdfDownloadRequest) => Promise<void>;
  isDownloading: (key: string) => boolean;
}

/** Downloads a server-rendered PDF, one render per key at a time. */
export function useServerPdfDownload(
  messages: ServerPdfDownloadMessages,
): UseServerPdfDownloadResult {
  // The ref refuses a second click before the re-render; the state drives spinners.
  const inFlightRef = useRef(new Set<string>());
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());

  const { preparing, failed } = messages;

  const download = useCallback(
    async ({ key, fetchBlob, fileName, analytics }: ServerPdfDownloadRequest) => {
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      setInFlight(new Set(inFlightRef.current));
      const notifId = notificationService.showInfo(preparing, { persist: true });
      try {
        const blob = await fetchBlob();
        await downloadFile({ blob, mimeType: 'application/pdf', fileName });
        track('document_downloaded', { ...analytics, format: 'pdf', downloader: 'loc' });
      } catch (err) {
        logger.error(`Failed to download ${analytics.document_type} PDF`, err);
        notificationService.showError(failed);
      } finally {
        notificationService.close(notifId);
        inFlightRef.current.delete(key);
        setInFlight(new Set(inFlightRef.current));
      }
    },
    [preparing, failed],
  );

  const isDownloading = useCallback((key: string) => inFlight.has(key), [inFlight]);

  return { download, isDownloading };
}
